import { autoinject } from 'aurelia-framework';
import { json } from 'aurelia-fetch-client';
import { ApiService } from '../../utils/api-service';
import { NodeModel, createTree } from '../../models/node-model';
import { INameIdAM } from '../../models/NameIdAM';

export interface IService {
	id: string;
	title: string;
	sortOrder: number;
	parentServiceId: string;
	serviceStructureId: string;
}

export interface AddServiceMessage {
	parentServiceId: string;
	serviceName: string;
	serviceCode: string;
	sortOrder?: number;
}

export class ServiceNodeModel extends NodeModel {
	code: string;
	dimensionValueIds: string[] = [];

	constructor(
		title: string,
		sortOrder: number = 1,
		parentName: string = null,
		id: string = null,
		code: string = null) {

		super(title, sortOrder, parentName, id, code);
	}
}

export interface ISelectedService {
	fiscalYearId: string;
	serviceId: string;
	customerMunicipalityId: string;
}

@autoinject
export class ServiceStructureService {
	static ServiceStructureNodeType = 'ServiceStructure';

	serviceStructureNodes: ServiceNodeModel[];

	constructor(private http: ApiService) {
		this.serviceStructureNodes = [];
	}

	async getServiceStructure(structureId: string): Promise<ServiceNodeModel[]> {
		const nodes = await this.http.fetchJson(`servicestructure/${structureId}`);

		// TODO: Clean up the createTree function. This doesn't actually return ServiceNodeModel instances,
		// but rather NodeModels that happen to have the code property as defined in ServiceNodeModel
		return createTree(nodes, null, (dto: any, node: ServiceNodeModel) => { node.code = dto.code; }) as ServiceNodeModel[];
	}

	async getServiceStructureForFiscalYear(structureId: string, fiscalYearId: string): Promise<ServiceNodeModel[]> {
		const nodes = await this.http.fetchJson(`servicestructure/${structureId}/for-fiscalyear/${fiscalYearId}`);

		// TODO: Clean up the createTree function. This doesn't actually return ServiceNodeModel instances,
		// but rather NodeModels that happen to have the code property as defined in ServiceNodeModel
		return createTree(nodes, null, (dto: any, node: ServiceNodeModel) => node.code = dto.code) as ServiceNodeModel[];
	}

	async getServiceStructureForCustomer(structureId: string, customerId: string): Promise<ServiceNodeModel[]> {
		const nodes = await this.http.fetchJson(`servicestructure/${structureId}/for-customer/${customerId}`);

		// TODO: Clean up the createTree function. This doesn't actually return ServiceNodeModel instances,
		// but rather NodeModels that happen to have the code property as defined in ServiceNodeModel
		return createTree(nodes, null, (dto: any, node: ServiceNodeModel) => { node.code = dto.code; }) as ServiceNodeModel[];
	}

	async getServiceStructuresOfCustomer(customerId: string): Promise<INameIdAM[]> {
		return await this.http.fetchJson(`servicestructure/of-customer/${customerId}`);
	}

	refreshServiceStructure(structureId: string, fiscalYearId: string = null): Promise<NodeModel[]> {
		this.serviceStructureNodes = undefined;
		return this.http.fetchJson(`servicestructure/${structureId}`).then(this.handleServiceStructureResponse.bind(this));
	}

	refreshServicesSelectedToFiscalYear(fiscalYearId: string, serviceStructureId: string) {
		this.serviceStructureNodes = undefined;
		return this.http.fetchJson(`servicestructure/${serviceStructureId}/for-fiscalyear/${fiscalYearId}`)
			.then(this.handleServiceStructureResponse.bind(this));
	}

	updateService(node: ServiceNodeModel): Promise<Response> {
		const service = this.nodeToService(node);

		return this.http.fetch('servicestructure/service/update', {
			method: 'post',
			body: json({
				id: service.id,
				name: service.title,
				sortOrder: service.sortOrder,
				parentServiceId: service.parentId,
				code: service.code
			})
		});
	}

	updateServiceStructure(node: NodeModel): Promise<Response> {
		const serviceStructure = this.nodeToServiceStructure(node);

		return this.http.fetch('servicestructure/update', {
			method: 'post',
			body: json(serviceStructure)
		});
	}

	addService(structureId: string, service: AddServiceMessage): Promise<Response> {
		return this.http.fetch(`servicestructure/${structureId}/add`, {
			method: 'post',
			body: json(service)
		});
	}

	isServiceDeletionAllowed(id: string): Promise<boolean> {
		return this.http.fetchJson(`servicestructure/service/${id}/is-delete-allowed`);
	}

	deleteService(serviceId: string): Promise<Response> {
		return this.http.fetch(`servicestructure/service/${serviceId}`, {
			method: 'delete'
		});
	}

	async getSelectedServicesForFiscalYear(customerId: string, fiscalYearId: string, serviceStructureId: string, partialMemberIds: string[]): Promise<ServiceSelections> {
		
		const fromServer = await this.http.fetchJson(`customers/${customerId}/measured-services/${fiscalYearId}/for-service-structure/${serviceStructureId}`);

		return {
			forCustomer: fromServer.filter(ss => !ss.customerMunicipalityId).map(s => s.serviceId),
			forPartialMembers: Object.assign({}, ...partialMemberIds.map(pmId =>
				({[pmId]: fromServer.filter(ss => ss.customerMunicipalityId === pmId).map(ss => ss.serviceId) })
			))
		};
	}

	async getServiceStructureCustomers(serviceStructureId: string): Promise<string[]> {
		return await this.http.fetchJson(`servicestructure/${serviceStructureId}/customers`);
	}

	saveServiceSelection(customerId: string, fiscalYearId: string, serviceStructureId: string, selectedIds: string[], selectedServiceIdsForPartialMembers: { [customerMunicipalityId: string]: string[] }): Promise<Response> {

		return this.http.fetch(`customers/${customerId}/measured-services/${fiscalYearId}/for-service-structure/${serviceStructureId}`, {
			method: 'post',
			body: json({
				forCustomer: selectedIds,
				forPartialMembers: selectedServiceIdsForPartialMembers
			})
		});
	}

	private handleServiceStructureResponse(services: any[]) {
		this.serviceStructureNodes = createTree(
			services,
			null,
			(dto: any, node: ServiceNodeModel) => node.code = dto.code
		) as ServiceNodeModel[];
		return this.serviceStructureNodes;
	}

	private nodeToService(node: ServiceNodeModel) {
		return {
			id: node.id,
			title: node.title,
			sortOrder: node.sortOrder,
			parentId: node.parent ? node.parent.id : node.parentId,
			code: node.code
		};
	}

	private nodeToServiceStructure(node: NodeModel) {
		return {
			id: node.id,
			name: node.title
		};
	}
}

export class ServiceSelections {

	forCustomer: string[];
	forPartialMembers: { [customerMunicipalityId: string]: string[] } = {}; // key: customerMunicipalityId, value: selected service-id's
}
